import { PALETTE } from '../theme';

/**
 * Shared visual style for choropleth map regions. Every standard MapSection
 * (USA states, country provinces, etc.) uses this so the look stays
 * consistent and the stroke / fill values can be tuned in ONE place.
 *
 * Why these specific numbers:
 *
 * - `STROKE` is a mid-grey, not white. White strokes vanish against the
 *   light-grey not-visited fill — fine on the live map at full size, but
 *   sub-pixel by the time the captured PNG is composited onto a cup or
 *   t-shirt preview. A darker mid-grey keeps borders visible there.
 *
 * - `STROKE_WIDTH` is 2.5 SVG units. The cup preview's design area is
 *   ~100px wide while the source SVG is 800px → 0.13× scale. At that
 *   downscale anything below ~2 SVG units anti-aliases away. 2.5 lands
 *   visible on previews without looking heavy on the live map.
 *
 * - `FILL_NOT_VISITED` is the standard "ghost" grey. PALETTE.textVeryFaint
 *   is the hover variant — slightly cooler so the cursor read is clear.
 *
 * If you need to change how borders look, edit ONE of these constants and
 * every map updates. Do not re-introduce inline `stroke: '#fff'` styles in
 * individual MapSection files.
 */
export const GEOGRAPHY_STROKE = '#909090';
export const GEOGRAPHY_STROKE_WIDTH = 1;
export const GEOGRAPHY_STROKE_WIDTH_HOVER = 1.5;
export const GEOGRAPHY_FILL_NOT_VISITED = '#e0e0e0';

/**
 * Standard react-simple-maps `<Geography>` style object for a region whose
 * visited state and accent colour are known.
 *
 * Usage:
 *   <Geography
 *       style={geographyStyles(isVisited, hexColor)}
 *       ...
 *   />
 */
export const geographyStyles = (isVisited, hexColor) => ({
    default: {
        fill: isVisited ? hexColor : GEOGRAPHY_FILL_NOT_VISITED,
        stroke: GEOGRAPHY_STROKE,
        strokeWidth: GEOGRAPHY_STROKE_WIDTH,
        outline: 'none',
        cursor: 'pointer',
    },
    hover: {
        fill: isVisited ? hexColor : PALETTE.textVeryFaint,
        stroke: GEOGRAPHY_STROKE,
        strokeWidth: GEOGRAPHY_STROKE_WIDTH_HOVER,
        outline: 'none',
        cursor: 'pointer',
    },
    pressed: {
        fill: hexColor,
        stroke: GEOGRAPHY_STROKE,
        strokeWidth: GEOGRAPHY_STROKE_WIDTH,
        outline: 'none',
    },
});

/* ============================================================================
 * MARKER LABEL OUTLINE — a separate concern from geography strokes
 *
 * The text labels that appear above markers on Caribbean (island names) and
 * National Parks (park names) use SVG `paint-order: stroke` to render a halo
 * around each glyph, so the text reads against any background colour.
 *
 * These values used to live as inline hardcoded `stroke: '#b8b8b8'` /
 * `strokeWidth: 2.5` in CaribbeanMapSection and NationalParksMapSection. They
 * visually mimicked the old geography stroke values and read as "captions
 * borrow the geography stroke" — they don't, but the source-of-truth confusion
 * was real. Centralising them here makes the separation explicit: labels are
 * NOT geographies. Tune label legibility independently of map borders.
 *
 * `LABEL_OUTLINE_COLOR` is white so the halo lifts text off any map colour
 * without competing with geography borders (which are grey). On the products
 * the captured PNG composites onto a white cup / white t-shirt, where the
 * halo blends in cleanly.
 * ========================================================================== */
export const LABEL_OUTLINE_COLOR = '#ffffff';
export const LABEL_OUTLINE_WIDTH = 2;
export const LABEL_OUTLINE_WIDTH_HOVER = 0; // hover state uses fill only — no halo
